import React, { useEffect, useRef } from "react";
import Highcharts, { Point, Series } from "highcharts";
import { HighChartRef, SynchroChartProps } from "./types";
import { BasicChart } from "./BasicChart";
import { oc } from "ts-optchain";

export const SynchroChart = ({ chartCollections, ...props }: SynchroChartProps) => {
  const chartRef = useRef<HighChartRef>();

  useEffect(() => {
    syncTooltip(chartCollections, chartRef.current);
  }, []);

  return <BasicChart ref={chartRef} {...props} />;
};

function syncTooltip(chartCollections: HighChartRef[], target?: HighChartRef) {
  if (!target || !target.container || !target.container.current) return;

  // Register chart into chartCollections
  const index = chartCollections.indexOf(target);
  index < 0 && chartCollections.push(target);
  const mouseMoveHandler = () => onMouseMove(target);
  target.container.current.addEventListener("mousemove", mouseMoveHandler);
  target.container.current.addEventListener("mouseleave", onMouseLeave);

  return () => {
    if (!target || !target.container || !target.container.current) return;

    // Unregister chart from chartCollections
    const index = chartCollections.indexOf(target);
    index >= 0 && chartCollections.splice(index, 1);
    target.container.current.removeEventListener("mousemove", mouseMoveHandler);
    target.container.current.removeEventListener("mouseleave", onMouseLeave);
  };

  function onMouseLeave() {
    //Hide all tooltip when mouse leave
    chartCollections.forEach(c => {
      if (!c || !c.chart) return;
      const { tooltip, series } = c.chart;
      tooltip && tooltip.hide();
      series && series.forEach(s => s.setState("normal"));
    });
  }

  function onMouseMove(target: HighChartRef) {
    const hoverPoint = oc(target).chart.hoverPoint();

    if (hoverPoint && chartCollections.length > 1) {
      chartCollections.forEach(c => {
        const series = oc(c).chart.series();

        if (!series || series.length === 0) return;

        const visibleSeries = series.filter(s => s.visible);
        const points = visibleSeries.reduce((memo, s) => [...memo, ...s.points], [] as Highcharts.Point[]);

        //hover over to all charts in synchronized collections
        hoverOver(hoverPoint, points, series);
      });
    }
  }

  function hoverOver(hoverPoint: Point, points: Point[], series: Series[]) {
    // For multiple series, both series name and category must be compared
    const targetPoint =
      series.length > 1
        ? points.find(item => item.name === hoverPoint.name && item.series.name === hoverPoint.series.name)
        : points.find(item => item.name === hoverPoint.name);

    if (targetPoint) {
      targetPoint.setState("hover");
      targetPoint.series.chart.tooltip.refresh(targetPoint);
      if (series.length > 1) {
        // Selected series should be hover & the rest will be inactive
        targetPoint.series.setState("hover");
        points
          .filter(item => item.category !== hoverPoint.category && item.series.name !== hoverPoint.series.name)
          .forEach(p => p.series.setState("inactive"));
      }
    } else {
      points.forEach(p => p.series.chart.tooltip.hide(0));
    }
  }
}
